import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '../../../icons/SvgIcons';
import { useCloseOnOutsideInteraction } from '../../../hooks/useCloseOnOutsideInteraction';
import {
  changeImpactOptions,
  changeImpactStyles,
  getChangeImpactLabel,
  SupportedChangeImpact
} from './ChangeImpactBadge';

type ChangeImpactDropdownProps = {
  selectedImpact: SupportedChangeImpact;
  canEdit: boolean;
  onSelect: (impact: SupportedChangeImpact) => Promise<void> | void;
};

export function ChangeImpactDropdown({
  selectedImpact,
  canEdit,
  onSelect
}: ChangeImpactDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const availableOptions = changeImpactOptions.filter(
    (option) => option !== selectedImpact
  );

  useCloseOnOutsideInteraction(isOpen, [triggerRef, menuRef], () =>
    setIsOpen(false)
  );

  const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!canEdit) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = availableOptions.length * 36 + 2;

    setMenuPosition({
      left: rect.left,
      top:
        rect.bottom + menuHeight + 4 > window.innerHeight
          ? rect.top - menuHeight - 4
          : rect.bottom + 4
    });
    setIsOpen((open) => !open);
  };

  const handleOptionClick = (impact: SupportedChangeImpact) => {
    setIsOpen(false);
    onSelect(impact);
  };

  return (
    <div className="relative" ref={triggerRef}>
      <button
        onClick={handleTriggerClick}
        disabled={!canEdit}
        className={`inline-flex items-center gap-0.5 rounded-[10px] h-5 pt-0.5 pb-0.5 pl-2 pr-1 ${
          changeImpactStyles[selectedImpact].badge
        } ${changeImpactStyles[selectedImpact].hover} ${
          changeImpactStyles[selectedImpact].focus
        } ${!canEdit ? 'opacity-70 cursor-not-allowed' : ''}`}
      >
        <span
          className={`font-inter font-medium text-xs leading-4 text-center ${changeImpactStyles[selectedImpact].text}`}
        >
          {getChangeImpactLabel(selectedImpact)}
        </span>
        <div className={changeImpactStyles[selectedImpact].stroke}>
          <ChevronDownIcon />
        </div>
      </button>

      {isOpen &&
        canEdit &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] min-w-[120px]"
            style={{
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`
            }}
          >
            {availableOptions.map((option) => (
              <button
                key={option}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(option);
                }}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 font-inter text-sm text-gray-700 first:rounded-t-lg last:rounded-b-lg"
              >
                {getChangeImpactLabel(option)}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
